/**
 * ProvenanceChip — small "Filled from <source>" pill rendered next to
 * any UI surface showing an auto-filled value.
 *
 * Design intent: tell the user *where* a value came from in one glance,
 * so they can decide whether to trust it. Same shape across the welcome
 * wizard, brief carousel, settings panels, and (future) lead/person
 * detail views.
 *
 * Sources mapped to short labels + icons:
 *   - website          → 📄 from your website
 *   - github           → 🐙 from your GitHub org
 *   - linkedin         → 💼 from LinkedIn
 *   - ats              → 🏢 from your ATS
 *   - integration      → 🔗 from a connected integration
 *   - repo             → 📂 from this repo
 *   - user             → 🖐 typed by you
 *   - default          → ✨ inferred
 *
 * Wraps an existing <Badge> atom (from `@excellent/ui`) — same chip
 * shape as the rest of the system. Click handler optional; when
 * provided, used to scope the tooltip's "View source" action (e.g.
 * open the source URL in a new tab).
 */


import * as React from "react";
import { cn } from "../utils";
import type { ProvenanceSource } from "../api/contracts/brief-meta";

// Single source of truth lives in the brief-meta contract; re-exported
// here (and onward from `@excellent/ui`) so existing imports keep working.
export type { ProvenanceSource };

export interface Provenance {
  source: ProvenanceSource;
  /** Original URL the value was scraped/derived from. */
  url?: string | null;
  /** Optional fragment ("footer copyright", "JSON-LD Organization.legalName") */
  selector?: string | null;
  /** 0..1 confidence — informs the rendering tone, not a hard gate. */
  confidence?: number | null;
}

export interface ProvenanceChipProps {
  provenance: Provenance;
  /** Optional override of the displayed label. Defaults to per-source label. */
  label?: string;
  /** Tooltip text. Defaults to a sensible per-source line. */
  title?: string;
  className?: string;
}

const SOURCE_PRESETS: Record<
  ProvenanceSource,
  { icon: string; label: string; tone: "user" | "high" | "medium" | "low" }
> = {
  website: { icon: "📄", label: "from your website", tone: "high" },
  github: { icon: "🐙", label: "from your GitHub org", tone: "high" },
  linkedin: { icon: "💼", label: "from LinkedIn", tone: "high" },
  ats: { icon: "🏢", label: "from your ATS", tone: "high" },
  integration: { icon: "🔗", label: "from a connected integration", tone: "medium" },
  repo: { icon: "📂", label: "from this repo", tone: "medium" },
  user: { icon: "🖐", label: "typed by you", tone: "user" },
  default: { icon: "✨", label: "inferred", tone: "low" },
};

export function ProvenanceChip({
  provenance,
  label,
  title,
  className,
}: ProvenanceChipProps) {
  const preset = SOURCE_PRESETS[provenance.source] ?? SOURCE_PRESETS.default;
  const text = label ?? preset.label;
  const tipParts: string[] = [];
  if (provenance.url) tipParts.push(provenance.url);
  if (provenance.selector) tipParts.push(provenance.selector);
  if (provenance.confidence != null) {
    tipParts.push(`confidence ${Math.round(provenance.confidence * 100)}%`);
  }
  const computedTitle = title ?? (tipParts.length ? tipParts.join(" • ") : text);

  const toneClass: Record<typeof preset.tone, string> = {
    user: "text-foreground",
    high: "text-muted-foreground",
    medium: "text-muted-foreground",
    low: "text-muted-foreground italic",
  };

  // Render as a plain inline-flex element rather than the Button atom —
  // a chip should never take focus by default. Anchor when url is present.
  const inner = (
    <span
      className={cn(
        "inline-flex items-center gap-1 type-subtext",
        toneClass[preset.tone],
        className,
      )}
      title={computedTitle}
      aria-label={computedTitle}
    >
      <span aria-hidden="true">{preset.icon}</span>
      <span>{text}</span>
    </span>
  );

  if (provenance.url) {
    return (
      <a
        href={provenance.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex hover:underline underline-offset-2"
      >
        {inner}
      </a>
    );
  }
  return inner;
}
